import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import {
  expandShapeSnapshotsForClipboard,
  expandTextSnapshotsForClipboard,
} from '@/lib/groups/clipboard-group-policy'
import { isGroupShape } from '@/lib/groups/group-kind'

/**
 * Every descendant shape and text box nested under `group`, at any depth (including through
 * nested sub-groups) — the group shape itself is excluded from the result. Built on the same
 * recursive expansion clipboard copy/paste already uses (`expandShapeSnapshotsForClipboard`/
 * `expandTextSnapshotsForClipboard`), so it's the single source of truth for "what's inside this
 * group" shared by Mirror Move/Copy eligibility checks and their execution.
 */
export function collectGroupMirrorMembers(
  group: ShapeData,
  allShapes: ShapeData[],
  allTextBoxes: TextBoxData[]
): { shapes: ShapeData[]; textBoxes: TextBoxData[] } {
  const expandedShapes = expandShapeSnapshotsForClipboard([group.id], allShapes)
  const shapes = expandedShapes.filter((s) => s.id !== group.id)
  const textBoxes = expandTextSnapshotsForClipboard({
    shapeSnapshots: expandedShapes,
    selectedTextIds: [],
    allTextBoxes,
    allShapes,
  })

  return { shapes, textBoxes }
}

/**
 * Folds every eligible-and-selected group in `selectedShapes` into its full descendant list (at
 * any nesting depth) and appends those to the flat shape/text lists Mirror Move/Copy already know
 * how to operate on directly — the group shell and each member are mirrored/cloned independently
 * across the same global axis, so no group-relative math is needed, just the full member list.
 * Callers must have already screened `selectedShapes`/`selectedTextBoxes` for mirror eligibility
 * (a group's eligibility already covers its whole subtree — see `isShapeMirrorEligible`/
 * `isShapeMirrorCopyEligible`) — this function only flattens membership, deduplicating in case a
 * descendant is somehow already present in the flat selection.
 */
export function expandMirrorSelectionMembers(
  selectedShapes: ShapeData[],
  selectedTextBoxes: TextBoxData[],
  allShapes: ShapeData[],
  allTextBoxes: TextBoxData[]
): { shapes: ShapeData[]; textBoxes: TextBoxData[] } {
  const shapes: ShapeData[] = []
  const textBoxes: TextBoxData[] = [...selectedTextBoxes]
  const seenShapeIds = new Set<string>()
  const seenTextIds = new Set(selectedTextBoxes.map((t) => t.id))

  for (const shape of selectedShapes) {
    if (seenShapeIds.has(shape.id)) continue
    seenShapeIds.add(shape.id)
    shapes.push(shape)

    if (!isGroupShape(shape)) continue

    const { shapes: members, textBoxes: memberTexts } = collectGroupMirrorMembers(
      shape,
      allShapes,
      allTextBoxes
    )

    for (const member of members) {
      if (seenShapeIds.has(member.id)) continue
      seenShapeIds.add(member.id)
      shapes.push(member)
    }

    for (const memberText of memberTexts) {
      if (seenTextIds.has(memberText.id)) continue
      seenTextIds.add(memberText.id)
      textBoxes.push(memberText)
    }
  }

  return { shapes, textBoxes }
}
